'use client'

import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from '@/i18n/client'

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

interface TurnstileProps {
  onVerify: (token: string) => void
  onError?: () => void
  onExpire?: () => void
  className?: string
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''

/**
 * Cloudflare Turnstile CAPTCHA Widget.
 * Falls kein NEXT_PUBLIC_TURNSTILE_SITE_KEY gesetzt ist,
 * wird das Widget nicht gerendert (Dev-Modus).
 */
export function Turnstile({ onVerify, onError, onExpire, className }: TurnstileProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const scriptLoadedRef = useRef(false)

  // Dev-Modus: Kein Site-Key → Widget nicht rendern, Token auto-setzen
  const isDevMode = !SITE_KEY

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: (token: string) => onVerify(token),
      'error-callback': () => onError?.(),
      'expired-callback': () => {
        onExpire?.()
        onVerify('')
      },
      theme: 'auto',
      size: 'normal',
    })
  }, [onVerify, onError, onExpire])

  useEffect(() => {
    if (isDevMode) {
      // Im Dev-Modus sofort "Token" setzen, damit das Formular funktioniert
      onVerify('dev-mode-bypass')
      return
    }

    // Turnstile-Script laden
    if (!scriptLoadedRef.current && !document.querySelector('script[src*="turnstile"]')) {
      scriptLoadedRef.current = true
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
      script.async = true
      window.onTurnstileLoad = renderWidget
      document.head.appendChild(script)
    } else if (window.turnstile) {
      renderWidget()
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // Widget wurde bereits entfernt
        }
        widgetIdRef.current = null
      }
    }
  }, [isDevMode, onVerify, renderWidget])

  // Dev-Modus: Nichts rendern
  if (isDevMode) return null

  return (
    <div className={className}>
      <div ref={containerRef} />
      <p className="text-[10px] text-[var(--muted)] mt-1">{t('captcha.verifying')}</p>
    </div>
  )
}
